import { useEffect, useState, useCallback } from 'react';
import properties from '@upgrade/themes/themes/upgrade';
const sizeToMedia = {
  xs: properties.mediaXs,
  sm: properties.mediaSm,
  md: properties.mediaMd,
  lg: properties.mediaLg
};
const cache = {};
const matchMedia = originalSize => {
  // some of the current usages are passing uppercase sizes, so keep it backwards compatible (please favor lowerCase
  // when possible for consistency)
  const size = originalSize.toLowerCase();
  let mediaQueryList = cache[size];
  if (!mediaQueryList) {
    mediaQueryList = window.matchMedia(`(min-width: ${sizeToMedia[size]})`);
    cache[size] = mediaQueryList;
  }
  return mediaQueryList;
};
const matches = size => {
  return matchMedia(size).matches;
};
const addChangeListener = (mediaQueryList, func) => {
  if (mediaQueryList.addEventListener) {
    mediaQueryList.addEventListener('change', func);
  } else {
    // Support for older browser versions: https://caniuse.com/mdn-api_mediaquerylistevent
    mediaQueryList.addListener(func);
  }
};
const removeChangeListener = (mediaQueryList, func) => {
  if (mediaQueryList.removeEventListener) {
    mediaQueryList.removeEventListener('change', func);
  } else {
    // Support for older browser versions: https://caniuse.com/mdn-api_mediaquerylistevent
    mediaQueryList.removeListener(func);
  }
};

// usage:
//
// const [minMd] = useWindowMinWidth('md');
//
// const [minXs, minSm, minMd, minLg] = useWindowMinWidth('xs', 'sm', 'md', 'lg');
//
const useWindowMinWidth = (...minSizes) => {
  // setCounter is used just to force a re-render
  const [, setCounter] = useState(1);
  const triggerRender = useCallback(() => {
    setCounter(cur => cur + 1);
  }, [setCounter]);
  useEffect(() => {
    if (!window?.matchMedia) {
      return;
    }
    const lists = minSizes.map(matchMedia);
    lists.forEach(mediaQueryList => {
      addChangeListener(mediaQueryList, triggerRender);
    });

    // eslint-disable-next-line consistent-return
    return () => {
      lists.forEach(mediaQueryList => {
        removeChangeListener(mediaQueryList, triggerRender);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...minSizes, triggerRender]);

  // TODO: create lint rule; or create util function
  if (typeof window === 'undefined') return [];
  return window?.matchMedia ? minSizes.map(matches) : minSizes.map(() => false);
};
export default useWindowMinWidth;
//# sourceMappingURL=useWindowMinWidth.js.map